// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'

import { ContractNameEnum } from './ContractCTO'
import { ERC20Wei } from './conversions'
import { ChainConfigName } from './DeploymentConfigs'
import { PoolTypeEnum, PoolTypeToString, yupErc20WeiRequired, yupEthAddrRequired } from './Enums'
import { PoolOffchainDataCTO, PoolOffchainDataSchemaValues } from './PoolOffchainDataCTO'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ZeroAddress } from 'ethers'

export const PoolBaseOverviewCTOSchemaValues = {
  version: yup
    .string()
    .label('Data Version')
    .required()
    .default('5.0.0')
    .meta(
      toFieldMetaData({ propName: 'version', description: 'Data Version ', stringType: StringTypeEnum.DATA_VERSION })
    ),
  id: yup
    .string()
    .label('PoolId poolAddr')
    .required()
    .default('ID_NOT_SET')
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of pool ', stringType: StringTypeEnum.ID })),
  name: yup
    .string()
    .required()
    .label('Vault Name')
    .meta(
      toFieldMetaData({
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
        propName: 'name',
        description: 'Name of the Loan Vault',
        example: 'TokenName',
      })
    ),
  symbol: yup
    .string()
    .required()
    .default(' ')
    .label('Token Symbol')
    .meta(
      toFieldMetaData({
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
        propName: 'symbol',
        description: 'Symbol for Vault Tokens',
        example: 'OTT',
      })
    ),
  poolType: yup
    .number<PoolTypeEnum>()
    .oneOf(Object.values(PoolTypeEnum).filter((v) => typeof v === 'number') as number[])
    .label('Pool Type')
    .defined()
    .default(PoolTypeEnum.NotPool)
    .meta(
      toFieldMetaData({
        propName: 'poolType',
        description: 'Type 0 is a term pool, type 1 is a flexible pool, type 2 is a dynamic pool.',
        stateToString: PoolTypeToString,
      })
    ),

  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  liquidityAssetAddr: yupEthAddrRequired
    .label('Liquidity Asset Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'liquidityAssetAddr',

        description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  liquidityTokenSymbol: yup
    .string()
    .required()
    .label('Symbol for Liquidity Token')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
        description: 'Symbol for the Liquidity Token',
        example: 'USDC',
      })
    ),
  currencyLabel: yup
    .string()
    .required()
    .label('Label for Liquidity Token')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'currencyLabel',
        description: 'Label for the Liquidity Token',
        example: 'USDC',
      })
    ),
  poolControllerAddr: yupEthAddrRequired
    .label('Vault Controller Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolControllerAddr',
        stringType: StringTypeEnum.ETH_ADDR,
        description: 'The smart-contract address for the Vault Controller. ',
      })
    ),

  borrowerManagerAddr: yupEthAddrRequired
    .label('Borrower Manager Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'borrowerManagerAddr',
        description: 'The wallet address of the Advisor.',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  poolAdminAddr: yupEthAddrRequired
    .label('Pool Administrator Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAdminAddr',
        description: 'The wallet address of the Pool Administrator',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  borrowerWalletAddr: yupEthAddrRequired
    .label('Borrower Wallet Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'borrowerWalletAddr',
        description: 'The wallet address of the Borrower. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  chainConfigurationName: yup
    .string()
    .required()
    .label('chainConfigurationName')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'chainConfigurationName',
        description: 'The name of the blockchain network configuration',
      })
    ),
  creationBlock: yup
    .number()
    .required()
    .default(0)
    .label('Creation Block Number')
    .meta(
      toFieldMetaData({
        propName: 'creationBlock',
        description: 'The block number when the pool was created',
      })
    ),
  creationTimestamp: yup
    .number()
    .required()
    .default(0)
    .label('Creation Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'creationTimestamp',
        description: 'The timestamp when the pool was created',
      })
    ),
  blockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .default(0)
    .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block number of the data' })),
  timestamp: yup
    .number()
    .label('Timestamp')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Timestamp of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME,
      })
    ),
  timestampDateString: yup
    .string()
    .required()
    .label('Timestamp Date')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'timestampDateString',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
        description: 'Timestamp Date of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
      })
    ),
  timestampString: yup
    .string()
    .required()
    .default(' ')
    .label('Timestamp Date Time')
    .meta(
      toFieldMetaData({
        propName: 'timestampString',
        description: 'Timestamp of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
      })
    ),
  dayNumber: yup
    .number()
    .label('Day Number')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'dayNumber',
        description: 'Day number of timestamp',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
      })
    ),
  timeOfDay: yup
    .number()
    .label('Time of Day')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'timeOfDay',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_TIME_OF_DAY,
      })
    ),

  chainId: yup
    .number()
    .required()
    .default(0)
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', example: '5', description: 'ChainId of the Data' })),

  totalOutstandingLoanPrincipal: yupErc20WeiRequired
    .label('Total Outstanding Loan Principal')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'totalOutstandingLoanPrincipal',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 6,
        description:
          'The total principal earning interest for the vault. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  /* OFFCHAIN DATA */

  ...PoolOffchainDataSchemaValues,
}

const PoolBaseOverviewCTOSchemaInfer = yup.object(PoolBaseOverviewCTOSchemaValues)
export const PoolBaseOverviewCTOSchema: yup.ObjectSchema<PoolBaseOverviewCTO> = PoolBaseOverviewCTOSchemaInfer
export interface PoolBaseOverviewCTO extends PoolOffchainDataCTO {
  symbol: string
  version: string
  id: string
  name: string

  poolType: PoolTypeEnum
  poolAddr: EthAddr

  liquidityAssetAddr: EthAddr
  liquidityTokenSymbol: string
  currencyLabel: string

  poolControllerAddr: EthAddr
  borrowerManagerAddr: EthAddr
  poolAdminAddr: EthAddr
  borrowerWalletAddr: EthAddr

  chainConfigurationName: string
  creationBlock: number
  creationTimestamp: number
  blockNumber: number

  timestamp: number
  timestampDateString: string
  timestampString: string
  dayNumber: number
  timeOfDay: number
  chainId: number
  totalOutstandingLoanPrincipal: ERC20Wei
}

type PoolBaseOverviewCTOInfer = InferType<typeof PoolBaseOverviewCTOSchemaInfer>

export const _testAPoolBaseOverview: PoolBaseOverviewCTO = {} as unknown as PoolBaseOverviewCTOInfer
export const _testBPoolBaseOverview: PoolBaseOverviewCTOInfer = {} as unknown as PoolBaseOverviewCTO

export const PoolContractSchema = yup.object({
  poolAddr: yupEthAddrRequired
    .required()
    .default(ZeroAddress)
    .label('Vault Address')
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  contractName: yup
    .mixed<ContractNameEnum>()
    .oneOf(Object.values(ContractNameEnum))
    .defined()
    .default(ContractNameEnum.Unknown)
    .label('Contract Name')
    .meta(
      toFieldMetaData({
        propName: 'contractName',
        description: 'Contract Name',
      })
    ),
  name: yup
    .string()
    .required()
    .default(' ')
    .label('Vault Name')
    .meta(toFieldMetaData({ propName: 'name', description: 'Name of the Loan Vault', example: 'TokenName' })),

  symbol: yup
    .string()
    .required()
    .label('symbol ')
    .meta(
      toFieldMetaData({
        propName: 'symbol',
        description: 'symbol if it is a ERC20 token',
      })
    ),
  poolType: yup
    .number<PoolTypeEnum>()
    .label('Pool Type')
    .required()
    .default(PoolTypeEnum.NotPool)
    .meta(
      toFieldMetaData({
        propName: 'poolType',
        description: 'Type 0 is a term pool, type 1 is a flexible pool, type 2 is a dynamic pool.',
        stateToString: PoolTypeToString,
      })
    ),
  chainConfigName: yup
    .string<ChainConfigName>()
    .required()
    .label('Chain Config Name')
    .meta(
      toFieldMetaData({
        propName: 'chainConfigName',
        description: 'Chain Config Name',
      })
    ),
  liquidityAssetAddr: yupEthAddrRequired
    .label('Liquidity Asset Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'liquidityAssetAddr',
        description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  liquidityTokenSymbol: yup
    .string()
    .required()
    .default(' ')
    .label('Symbol for Liquidity Token')
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',
        description: 'Symbol for the Liquidity Token',
        example: 'USDC',
      })
    ),
  currencyLabel: yup
    .string()
    .required()
    .default(' ')
    .label('Label for Liquidity Token')
    .meta(
      toFieldMetaData({
        propName: 'currencyLabel',
        description: 'Label for the Liquidity Token',
        example: 'USDC',
      })
    ),
})
